import { useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Save, ShieldAlert, Mail, RefreshCw, Trash2, LogIn } from "lucide-react";
import { atlasFetch, keyMode, signOut } from "@/lib/atlas-api";
import { Card, Skeleton } from "@/components/portal/PortalPrimitives";

function fmtDate(s) { if (!s) return "—"; try { return new Date(s).toLocaleString(); } catch { return s; } }

function KeyCard({
  envValue,         // "test" | "live"
  envLabel,         // "Sandbox" | "Production"
  envBadge,         // help text
  masked,
  rotatedAt,
  isCurrent,
  onSave,
  onClear,
  saving,
  clearing,
  testIdRoot,
}) {
  const [draft, setDraft] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    await onSave(envValue, trimmed);
    setDraft("");
  };

  const handleClear = async () => {
    if (!window.confirm(`Clear the ${envLabel.toLowerCase()} key? The current value will be removed and you'll need to paste a new one to authenticate.`)) return;
    await onClear(envValue);
  };

  return (
    <Card testId={`${testIdRoot}-card`}>
      <div>
        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{envLabel} key</p>
        <p className="mt-1 font-mono text-[14.5px] text-[#0A1128] break-all">
          {masked || <span className="text-slate-400 font-sans not-italic text-[13px]">Not set — paste a key below</span>}
        </p>
        {masked && (
          <p className="mt-1.5 text-[12px] text-slate-500">Last updated {fmtDate(rotatedAt)}</p>
        )}
        {isCurrent && (
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold bg-[#1A4232] text-[#F5F1EB]">
            CURRENTLY SIGNED IN WITH THIS KEY
          </span>
        )}
      </div>
      <p className="mt-3 text-[12px] text-slate-500">{envBadge}</p>

      {isCurrent && (
        <div className="mt-4 rounded-[10px] border border-amber-300 bg-amber-50/70 px-3 py-2.5 text-[12px] text-amber-900 leading-relaxed">
          <strong>Heads up:</strong> you're currently signed in with this {envLabel.toLowerCase()} key. Saving a new value rotates it immediately. For security, you'll need to sign back in with the new key to continue using the portal.
        </div>
      )}

      <form onSubmit={submit} className="mt-4 rounded-[12px] border-2 border-[#1A4232]/25 bg-[#F5F1EB]/60 p-4 space-y-3">
        <div>
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#1A4232] font-bold flex items-center gap-1.5">
            <RefreshCw className="h-3 w-3" />
            {masked ? `Replace ${envLabel.toLowerCase()} key` : `Add your ${envLabel.toLowerCase()} key`}
          </label>
          <p className="mt-1 text-[12px] text-slate-600">
            {masked
              ? `Paste a new ${envLabel.toLowerCase()} key — it replaces the current value immediately.`
              : `You don't have a ${envLabel.toLowerCase()} key registered yet. Paste one below and click Save.`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={envValue === "live" ? "afg_live_…" : "afg_test_…"}
            data-testid={`${testIdRoot}-input`}
            autoComplete="off"
            spellCheck="false"
            className="flex-1 min-w-0 rounded-[10px] border-2 border-slate-300 bg-white px-3 py-2.5 text-[14px] font-mono outline-none focus:border-[#1A4232] focus:ring-2 focus:ring-[#1A4232]/20 placeholder:text-slate-400"
          />
          <button
            type="submit"
            disabled={saving || !draft.trim()}
            data-testid={`${testIdRoot}-save`}
            className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[13px] font-semibold px-6 py-2.5 hover:bg-[#0E2A20] transition-colors disabled:bg-[#1A4232]/60 disabled:cursor-not-allowed"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? "Saving…" : "Save key"}
          </button>
        </div>
        {masked && !isCurrent && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleClear}
              disabled={clearing}
              data-testid={`${testIdRoot}-clear`}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 hover:text-red-700 underline-offset-4 hover:underline disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3" />
              {clearing ? "Clearing…" : "Clear this key"}
            </button>
          </div>
        )}
      </form>
    </Card>
  );
}

export default function PortalKeys() {
  const { me, refreshMe } = useOutletContext();
  const [busy, setBusy] = useState({});
  // useRef is synchronous — survives React state batching so back-to-back taps
  // (common on mobile / double-clicks) can't fire two concurrent PUTs.
  const inFlight = useRef({});
  // When the active session key is detected dead (401 from /keys), show a
  // recovery modal with a one-tap "Sign in again" CTA instead of just a toast.
  const [reauthOpen, setReauthOpen] = useState(false);
  const currentMode = keyMode();

  const promptReauth = () => setReauthOpen(true);
  const doReauth = () => {
    signOut();   // clears localStorage and redirects to /portal/login
  };

  const saveKey = async (envValue, newKey) => {
    const lockKey = `save_${envValue}`;
    if (inFlight.current[lockKey]) return;
    inFlight.current[lockKey] = true;
    setBusy((b) => ({ ...b, [envValue]: true }));
    try {
      const r = await atlasFetch("/api/v1/keys", {
        method: "PUT",
        headers: { "X-Atlas-Env": envValue },
        body: JSON.stringify({ environment: envValue, key: newKey }),
        skipAutoSignOut: true,
      });
      const newMode = newKey.startsWith("afg_live_") ? "production" : "sandbox";
      const patchKey = envValue === "live" ? "production_key_masked" : "sandbox_key_masked";
      const patchTs  = envValue === "live" ? "production_key_rotated_at" : "sandbox_key_rotated_at";
      refreshMe?.({ [patchKey]: r.key_masked, [patchTs]: r.saved_at });
      if (newMode === currentMode) {
        // The partner just rotated the SAME env they were signed in with. For
        // security, force them to re-authenticate with the new value — don't
        // auto-switch the session. The recovery modal handles the redirect.
        toast.success(
          `${envValue === "live" ? "Production" : "Sandbox"} key rotated (${r.key_masked}). Sign back in with the new value to continue.`,
        );
        promptReauth();
      } else {
        toast.success(
          `${envValue === "live" ? "Production" : "Sandbox"} key updated (${r.key_masked}). The new key now authenticates that environment.`,
        );
      }
    } catch (e) {
      if (e.status === 401) {
        promptReauth();
      } else if (e.status === 409) {
        toast.error("That key value is already registered to another partner. Generate a different one.");
      } else {
        toast.error(e.message);
      }
    } finally {
      inFlight.current[lockKey] = false;
      setBusy((b) => { const c = { ...b }; delete c[envValue]; return c; });
    }
  };

  const clearKey = async (envValue) => {
    const lockKey = `clear_${envValue}`;
    if (inFlight.current[lockKey]) return;
    inFlight.current[lockKey] = true;
    setBusy((b) => ({ ...b, [`clear_${envValue}`]: true }));
    try {
      await atlasFetch(`/api/v1/keys/${envValue}`, {
        method: "DELETE",
        headers: { "X-Atlas-Env": envValue },
        skipAutoSignOut: true,
      });
      const patchKey = envValue === "live" ? "production_key_masked" : "sandbox_key_masked";
      const patchTs  = envValue === "live" ? "production_key_rotated_at" : "sandbox_key_rotated_at";
      refreshMe?.({ [patchKey]: null, [patchTs]: null });
      toast.success(`${envValue === "live" ? "Production" : "Sandbox"} key cleared. Paste a new value to restore access.`);
    } catch (e) {
      if (e.status === 401) {
        promptReauth();
      } else {
        toast.error(e.message);
      }
    } finally {
      inFlight.current[lockKey] = false;
      setBusy((b) => { const c = { ...b }; delete c[`clear_${envValue}`]; return c; });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">API Keys</p>
        <h1 className="font-display text-[28px] text-[#0A1128] mt-1.5">Your API keys</h1>
        <p className="mt-2 text-[13.5px] text-slate-600 max-w-2xl">
          Live keys (<code className="font-mono">afg_live_…</code>) hit production. Test keys (<code className="font-mono">afg_test_…</code>) hit the sandbox. Paste a new value below to rotate either key. For security, rotating a key immediately invalidates the current session — you'll be asked to sign back in with the new value.
        </p>
      </div>

      {!me ? (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card><Skeleton className="h-4 w-1/2" /><Skeleton className="mt-2 h-4 w-2/3" /></Card>
          <Card><Skeleton className="h-4 w-1/2" /><Skeleton className="mt-2 h-4 w-2/3" /></Card>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <KeyCard
            envValue="live"
            envLabel="Production"
            envBadge="Used for live partners. Treat this value as a secret."
            masked={me.production_key_masked}
            rotatedAt={me.production_key_rotated_at}
            isCurrent={currentMode === "production"}
            onSave={saveKey}
            onClear={clearKey}
            saving={busy.live}
            clearing={busy.clear_live}
            testIdRoot="keys-production"
          />
          <KeyCard
            envValue="test"
            envLabel="Sandbox"
            envBadge="Used for testing. Calls to sandbox don't affect production usage counters."
            masked={me.sandbox_key_masked}
            rotatedAt={me.sandbox_key_rotated_at}
            isCurrent={currentMode === "sandbox"}
            onSave={saveKey}
            onClear={clearKey}
            saving={busy.test}
            clearing={busy.clear_test}
            testIdRoot="keys-sandbox"
          />
        </div>
      )}

      <Card testId="keys-card-contact" className="bg-[#F5F1EB]">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-[8px] bg-[#1A4232] text-[#F5F1EB] shrink-0">
            <Mail className="h-4 w-4" />
          </span>
          <div className="text-[13px] text-[#1A4232] leading-relaxed">
            <p className="font-display text-[17px] text-[#1A4232]">Lost a key or need a fresh secret?</p>
            <p className="mt-1.5">
              For security, AtlasForge only issues fresh keys and reveals ingest secrets on direct request. Email{" "}
              <a href="mailto:customersupport@atlasforgefinancial.com" className="underline font-semibold">
                customersupport@atlasforgefinancial.com
              </a>{" "}
              from the address on file and we'll handle it within one business day.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-start gap-3">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] bg-amber-50 text-amber-700 shrink-0">
            <ShieldAlert className="h-4 w-4" />
          </span>
          <div className="text-[13px] text-slate-700 leading-relaxed">
            <p className="font-semibold text-[#0A1128]">Security best practices</p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>Store keys in your backend's secret manager — never in client-side code.</li>
              <li>Rotate at least quarterly, and immediately after a suspected compromise.</li>
              <li>Use separate sandbox keys per environment (dev / staging) for clean logs.</li>
            </ul>
          </div>
        </div>
      </Card>

      {reauthOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A1128]/55 backdrop-blur-sm px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reauth-title"
          data-testid="reauth-modal"
        >
          <div className="w-full max-w-md rounded-[16px] bg-[#F5F1EB] border border-[#1A4232]/15 shadow-2xl p-6">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1A4232] text-[#F5F1EB] shrink-0">
                <ShieldAlert className="h-5 w-5" />
              </span>
              <div>
                <h2 id="reauth-title" className="font-display text-[20px] text-[#0A1128] leading-tight">
                  Rotation complete — sign in again
                </h2>
                <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
                  Your active key was rotated (either from here or from your Safe to Spend 365 admin), so this session is no longer valid. This is expected behavior — rotating any key invalidates outstanding sessions for security.
                </p>
                <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
                  Sign in again with your <strong>latest</strong> key value to continue. You can find it in your Safe to Spend 365 admin or by asking your AtlasForge admin for the current value.
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row-reverse gap-2">
              <button
                type="button"
                onClick={doReauth}
                data-testid="reauth-signin-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[13.5px] font-semibold px-5 py-2.5 hover:bg-[#0E2A20] transition-colors"
              >
                <LogIn className="h-4 w-4" />
                Sign in with latest key
              </button>
              <button
                type="button"
                onClick={() => setReauthOpen(false)}
                data-testid="reauth-dismiss-btn"
                className="inline-flex items-center justify-center rounded-full border border-slate-300 text-[#0A1128] text-[13px] font-semibold px-4 py-2.5 hover:bg-white transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
